<script lang="ts">
  import { Dialog } from 'bits-ui';
  import { fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { Label, PinInput, REGEXP_ONLY_DIGITS, Button } from 'bits-ui';
  import { mfaChallenge, type MfaChallenge, api } from '$lib/api.js';
  import { auth } from '$lib/stores/auth.js';
  import { get } from 'svelte/store';

  type Step = 'choose' | 'otp' | 'passkey';

  let challenge = $state<MfaChallenge | null>(null);
  let step = $state<Step>('choose');
  let otpCode = $state('');
  let loading = $state(false);
  let error = $state('');

  let email = $derived(get(auth).email ?? '');
  let open = $derived(challenge !== null);

  $effect(() => {
    const unsub = mfaChallenge.subscribe((c) => {
      challenge = c;
      if (c) {
        step = 'choose';
        otpCode = '';
        error = '';
      }
    });
    return unsub;
  });

  function dismiss() {
    challenge?.reject(new Error('MFA dismissed'));
  }

  async function handlePasskey() {
    loading = true;
    error = '';
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const options = await api.passkeys.loginOptions(email);
      const response = await startAuthentication({ optionsJSON: options as any });
      const data = await api.passkeys.loginVerify(email, response as any);
      auth.setTokens(data.accessToken);
      challenge?.resolve(data.accessToken);
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'NotAllowedError') {
        error = '';
      } else {
        error = e instanceof Error ? e.message : 'Passkey verification failed';
      }
    } finally {
      loading = false;
    }
  }

  async function handleOtp() {
    if (otpCode.length !== 6) return;
    loading = true;
    error = '';
    try {
      const data = await api.auth.verifyOtp(email, otpCode);
      auth.setTokens(data.accessToken);
      challenge?.resolve(data.accessToken);
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Invalid code';
      otpCode = '';
    } finally {
      loading = false;
    }
  }

  async function sendOtp() {
    loading = true;
    error = '';
    try {
      await api.auth.sendOtp(email);
      step = 'otp';
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Failed to send code';
    } finally {
      loading = false;
    }
  }
</script>

<Dialog.Root
  {open}
  onOpenChange={(v: boolean) => { if (!v) dismiss(); }}
>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 z-[60] bg-black/40 backdrop-blur-xs" />
    <Dialog.Content
      forceMount
      class="fixed inset-x-4 bottom-0 top-auto z-[60] mx-auto flex max-h-[min(88vh,36rem)] w-full max-w-md flex-col rounded-t-2xl bg-card p-5 shadow-[0_-12px_32px_rgba(var(--shadow-tint),0.12)] outline-hidden focus:outline-hidden dark:shadow-[0_-12px_32px_rgba(0,0,0,0.45)] sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:shadow-[0_12px_32px_rgba(var(--shadow-tint),0.12)]"
    >
      {#snippet child({ props, open: contentOpen })}
      {#if contentOpen}
      <div {...props} transition:fly={{ y: 100, duration: 300, easing: cubicOut }}>
      <div class="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-muted sm:hidden" aria-hidden="true"></div>
      <Dialog.Title class="font-display text-lg font-bold text-foreground">Verify it's you</Dialog.Title>
      <Dialog.Description class="mt-1 text-sm text-muted-foreground">
        For your security, payment actions require re-verification.
      </Dialog.Description>

      <div class="mt-5 flex flex-col gap-3">
        {#if step === 'choose'}
          <Button.Root
            onclick={handlePasskey}
            disabled={loading}
            class="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {#if loading}
              <span class="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"></span>
            {:else}
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"/>
                <path d="M20 21a8 8 0 1 0-16 0"/>
              </svg>
            {/if}
            Verify with passkey
          </Button.Root>

          <button
            onclick={sendOtp}
            disabled={loading}
            class="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
          >
            Use email code instead
          </button>

        {:else if step === 'otp'}
          <p class="text-sm text-muted-foreground">
            Enter the 6-digit code sent to <span class="font-medium text-foreground">{email}</span>.
          </p>

          <Label.Root class="flex flex-col gap-2">
            <PinInput.Root
              bind:value={otpCode}
              maxlength={6}
              pattern={REGEXP_ONLY_DIGITS}
              textalign="center"
              onComplete={() => void handleOtp()}
              class="group/pin flex justify-center gap-1.5 text-foreground"
            >
              {#snippet children({ cells }: { cells: { char: string | null | undefined; isActive: boolean; hasFakeCaret: boolean }[] })}
                {#each cells as cell, i (i)}
                  <PinInput.Cell
                    {cell}
                    class="relative flex h-14 min-w-10 items-center justify-center rounded-2xl border border-border bg-muted/70 text-2xl font-bold tabular-nums text-foreground outline-hidden data-active:ring-2 data-active:ring-brand-600 dark:bg-muted/40"
                  >
                    {#if cell.char != null && cell.char !== ''}
                      {cell.char}
                    {/if}
                    {#if cell.hasFakeCaret}
                      <span class="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
                        <span class="h-7 w-px animate-pulse bg-foreground/70"></span>
                      </span>
                    {/if}
                  </PinInput.Cell>
                {/each}
              {/snippet}
            </PinInput.Root>
          </Label.Root>

          <Button.Root
            onclick={handleOtp}
            disabled={loading || otpCode.length !== 6}
            class="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {#if loading}
              <span class="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"></span>
            {/if}
            Verify
          </Button.Root>

          <button
            onclick={() => { step = 'choose'; otpCode = ''; error = ''; }}
            class="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Back
          </button>
        {/if}

        {#if error}
          <p class="text-sm text-destructive">{error}</p>
        {/if}
      </div>
      </div>
      {/if}
      {/snippet}
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
